import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useEditor, EditorContent } from '@tiptap/react'
import { richTextDisplayExtensions } from '@/components/ui/minimal-tiptap/display-extensions'
import '@/components/ui/minimal-tiptap/styles/index.css'
import { Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import NewsTagBadge from './NewsTagBadge'
import SeoHead from '../../components/SeoHead'
import { htmlExcerpt, siteTitle } from '../../helpers/seo'

const GlobalNewsItem = ({
  item,
  inList = false,
  canManage,
  newsPath,
  hideBack = false,
}: {
  item: wowaudit.GlobalNewsItem
  inList?: boolean
  canManage?: boolean
  newsPath?: string
  hideBack?: boolean
}) => {
  const itemPath = newsPath ? `${newsPath}/${item.id}` : `${item.id}`
  const editPath = newsPath ? `${newsPath}/${item.id}/edit` : 'edit'

  const editor = useEditor({
    extensions: richTextDisplayExtensions,
    content: item.content,
    editable: false,
    immediatelyRender: false,
  })

  const editLink = (
    <Link
      to={editPath}
      className={
        inList ? '' : buttonVariants({ variant: 'outline', size: 'sm' })
      }
    >
      <Pencil className='w-4 h-4' />
    </Link>
  )

  return (
    <Card className='p-0'>
      {!inList ? (
        <SeoHead
          title={siteTitle([item.title || 'News'])}
          siteName='wowaudit'
          description={htmlExcerpt(item.content)}
          ogType='article'
          ogImage={item.mainImageUrl}
          robots={item.published ? 'index, follow' : 'noindex, nofollow'}
        />
      ) : null}
      <header
        className={cn(
          'p-4 border-b',
          inList ? 'border-accent' : 'border-border',
        )}
      >
        <div className='flex justify-between gap-2'>
          <div className='flex items-center gap-3 min-w-0'>
            {item.mainImageUrl && (
              <img
                src={item.mainImageUrl}
                alt={item.title}
                className='w-24 h-18 object-cover rounded-md border border-border shrink-0'
              />
            )}
            <div className='min-w-0'>
              {inList ? (
                <h3 className='mb-0.5'>
                  <Link to={itemPath}>{item.title}</Link>
                </h3>
              ) : (
                <h2 className='mb-0.5'>{item.title}</h2>
              )}
              <div className='flex flex-wrap items-center gap-1.5 text-muted-foreground'>
                {item.firstPublishedAt && (
                  <time dateTime={item.firstPublishedAt}>
                    {dayjs(item.firstPublishedAt).format('MMMM D, YYYY')}
                  </time>
                )}
                {item.createdBy && (
                  <>
                    <span>•</span>
                    <span>
                      {item.createdBy.obfuscatedBattletag ?? 'Hidden user'}
                    </span>
                  </>
                )}
                <div className='ml-2'>
                  {item.tags.map((tag) => (
                    <NewsTagBadge key={tag} tag={tag} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className='flex items-center gap-2 shrink-0'>
            {inList || hideBack ? null : (
              <Link
                to={newsPath ?? '..'}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                Back to news
              </Link>
            )}
            {canManage ? editLink : null}
          </div>
        </div>
      </header>

      <EditorContent
        editor={editor}
        className='minimal-tiptap-editor max-w-none p-4 cursor-unset'
      />
    </Card>
  )
}

export default GlobalNewsItem
